/**
 * Bulk Edit Sheet
 * Allows editing multiple items at once by selecting fields to update
 */

import { useState, useMemo } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Loader2 } from 'lucide-react';
import type { Field } from '@/hooks/useFields';

interface BulkEditSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    fields: Field[];
    selectedCount: number;
    onSave: (updates: Record<string, any>) => Promise<void>;
}

export function BulkEditSheet({
    open,
    onOpenChange,
    fields,
    selectedCount,
    onSave,
}: BulkEditSheetProps) {
    const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
    const [formData, setFormData] = useState<Record<string, any>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Filter out system fields and readonly fields
    const editableFields = useMemo(() => {
        return fields.filter((field) => {
            // Exclude system fields
            if (field.field.startsWith('date_') || field.field.startsWith('user_')) {
                return false;
            }
            // Exclude primary key
            if (field.schema?.is_primary_key) {
                return false;
            }
            return true;
        });
    }, [fields]);

    const handleFieldToggle = (fieldName: string, checked: boolean) => {
        const newSelected = new Set(selectedFields);
        if (checked) {
            newSelected.add(fieldName);
        } else {
            newSelected.delete(fieldName);
            // Remove from form data when unchecked
            const newFormData = { ...formData };
            delete newFormData[fieldName];
            setFormData(newFormData);
        }
        setSelectedFields(newSelected);
    };

    const handleFieldChange = (fieldName: string, value: any) => {
        setFormData({
            ...formData,
            [fieldName]: value,
        });
    };

    const handleSave = async () => {
        // Only send data for selected fields
        const updates: Record<string, any> = {};
        selectedFields.forEach((fieldName) => {
            updates[fieldName] = formData[fieldName];
        });

        if (Object.keys(updates).length === 0) {
            return;
        }

        setIsSubmitting(true);
        try {
            await onSave(updates);
            onOpenChange(false);
            // Reset state
            setSelectedFields(new Set());
            setFormData({});
        } catch (error) {
            console.error('Bulk edit failed:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        setSelectedFields(new Set());
        setFormData({});
        onOpenChange(false);
    };

    // Render field input based on type and interface
    const renderFieldInput = (field: Field) => {
        const value = formData[field.field];
        const onChange = (newValue: any) => handleFieldChange(field.field, newValue);
        const interfaceType = field.meta?.interface;
        const fieldType = field.type;

        // Boolean fields (checkbox, toggle)
        if (fieldType === 'boolean' || interfaceType === 'boolean') {
            return (
                <div className="flex items-center space-x-2">
                    <Switch
                        checked={!!value}
                        onCheckedChange={onChange}
                    />
                    <span className="text-sm text-muted-foreground">
                        {value ? 'Enabled' : 'Disabled'}
                    </span>
                </div>
            );
        }

        // Dropdown/Select fields
        if (interfaceType === 'select-dropdown') {
            const choices = (field.meta?.options?.choices as Array<{ text: string; value: string }>) || [];
            if (choices.length > 0) {
                return (
                    <Select value={value || ''} onValueChange={onChange}>
                        <SelectTrigger>
                            <SelectValue placeholder={`Select ${field.meta?.note || field.field}`} />
                        </SelectTrigger>
                        <SelectContent>
                            {choices.map((choice) => (
                                <SelectItem key={choice.value} value={choice.value}>
                                    {choice.text}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                );
            }
        }

        // Text area for longer text
        if (interfaceType === 'textarea' || interfaceType === 'input-multiline') {
            return (
                <Textarea
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={`Enter ${field.meta?.note || field.field}`}
                    rows={3}
                />
            );
        }

        // Number input
        if (fieldType === 'integer' || fieldType === 'bigInteger' || fieldType === 'float' || fieldType === 'decimal') {
            return (
                <Input
                    type="number"
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
                    placeholder={`Enter ${field.meta?.note || field.field}`}
                />
            );
        }

        // Date/DateTime input
        if (fieldType === 'date' || fieldType === 'timestamp' || interfaceType === 'datetime') {
            return (
                <Input
                    type="datetime-local"
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={`Select ${field.meta?.note || field.field}`}
                />
            );
        }

        // Default to text input
        return (
            <Input
                type="text"
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder={`Enter ${field.meta?.note || field.field}`}
            />
        );
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="w-full sm:max-w-2xl">
                <SheetHeader>
                    <SheetTitle>Bulk Edit {selectedCount} Items</SheetTitle>
                    <SheetDescription>
                        Select the fields you want to update and enter the new values.
                        All selected items will be updated with these values.
                    </SheetDescription>
                </SheetHeader>

                <Separator className="my-4" />

                <ScrollArea className="h-[calc(100vh-240px)]">
                    <div className="space-y-4 pr-4">
                        <p className="text-sm text-muted-foreground">
                            Check the fields you want to edit, then enter the values below.
                        </p>

                        {editableFields.map((field) => {
                            const isSelected = selectedFields.has(field.field);

                            return (
                                <div key={field.field} className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                        <Checkbox
                                            id={`field-${field.field}`}
                                            checked={isSelected}
                                            onCheckedChange={(checked) =>
                                                handleFieldToggle(field.field, checked as boolean)
                                            }
                                        />
                                        <Label
                                            htmlFor={`field-${field.field}`}
                                            className="flex-1 cursor-pointer font-medium"
                                        >
                                            {field.meta?.note || field.field}
                                        </Label>
                                    </div>

                                    {isSelected && (
                                        <div className="ml-6">
                                            {renderFieldInput(field)}
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {editableFields.length === 0 && (
                            <p className="text-sm text-muted-foreground">
                                No editable fields available.
                            </p>
                        )}
                    </div>
                </ScrollArea>

                <Separator className="my-4" />

                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={selectedFields.size === 0 || isSubmitting}
                    >
                        {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Update {selectedCount} Items
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}
